/* eslint-disable @typescript-eslint/no-explicit-any */
"use no memo";
import {
  Column,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedUniqueValues,
  getFacetedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowPinningState,
  RowSelectionState,
  SortingState,
  ColumnFiltersState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import type { Table as ReactTable } from "@tanstack/react-table";
import TablePagination from "./TablePagination";
import {
  Button,
  Chip,
  Icon,
  LoadingSpinner,
  PopupCard,
  Toggle,
} from "@app-components";
import {
  CSSProperties,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import { useLocalState } from "@/hooks/useLocalState";
import { useSessionState } from "@/hooks/useSessionState";
import {
  dateFilterFn,
  getWeekdayFromNumber,
  multiStringIncludesFilterFn,
  arrayIncludesFilterFn,
  forcedBooleanFilterFn,
} from "@/lib/utils";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { TableFilter } from "./TableFilter";
import { HAS_VALUE_MARKER, NO_VALUE_MARKER } from "@/lib/constants";
import { AnimatePresence, motion } from "framer-motion";

const filterChipAnimation = {
  initial: {
    opacity: 0,
    x: -10,
    width: 0,
    height: 0,
  },
  animate: {
    opacity: 1,
    x: 0,
    width: "auto",
    height: "auto",
    transition: {
      duration: 0.2,
      opacity: { delay: 0.2, duration: 0.2 },
      x: { delay: 0.2, duration: 0.2 },
    },
  },
  exit: {
    opacity: 0,
    x: -10,
    width: 0,
    height: 0,
    transition: {
      duration: 0.2,
      width: { delay: 0.2, duration: 0.2 },
      height: { delay: 0.2, duration: 0.2 },
    },
  },
};

export type FilterConfig<T> = {
  getLabel?: (value: any, column: Column<T, unknown> | undefined) => any;
  forceBoolean?: boolean;
  exclude?: boolean;
  includeInFilter?: boolean; // include hidden columns in filter panel
  mutuallyExclusive?: boolean; // whether options can be combined or if they are mutually exclusive (disable other options when one is selected) - default true
  optionValues?: any[];
};

interface TableProps<T> {
  name: string;
  data?: T[];
  columns: any[];
  isLoading?: boolean;
  rowSelection?: RowSelectionState;
  setRowSelection?: Dispatch<SetStateAction<RowSelectionState>>;
  onRowClick?: (rowData: T) => void;
  onRowHover?: (rowData: T) => void;
  getRowClassName?: (rowData: T) => string;
  noDataMessage?: string;
  className?: string;
  customPageSizeOptions?: number[];
  dark?: boolean;
  withPagination?: boolean;
  enableColumnControls?: boolean;
  enableFiltering?: boolean;
  /**
   * Dependencies that should cause filter menu to re-render (for example to keep option labels up-to-date)
   */
  filterDeps?: any[];
  defaultSorting?: SortingState;
  defaultColumnVisibility?: VisibilityState;
  enableMultiRowSelection?: boolean;
  enableRowSelection?: boolean;
  onTableReady?: (table: ReactTable<T>) => void;
  draggableRows?: boolean;
  onRowOrderChange?: (oldIndex: number, newIndex: number) => void;
  defaultFiltering?: ColumnFiltersState;
}

const getDefaultColumnOrder = (columns: any[]) =>
  columns.map((col) => col.id || (col.accessorKey as string));

function Table<T>({
  name,
  data,
  columns,
  isLoading,
  rowSelection = {},
  setRowSelection,
  onRowClick,
  onRowHover,
  getRowClassName,
  noDataMessage,
  className,
  customPageSizeOptions,
  dark,
  withPagination = true,
  enableColumnControls = false,
  enableFiltering,
  filterDeps,
  defaultSorting,
  defaultColumnVisibility,
  enableMultiRowSelection,
  enableRowSelection,
  onTableReady,
  draggableRows,
  onRowOrderChange,
  defaultFiltering,
}: TableProps<T>) {
  "use no memo";
  const { t, i18n } = useTranslation();
  const [sorting, setSorting] = useSessionState<SortingState>(
    `${name}TableSorting`,
    defaultSorting ?? [],
  );
  const [columnVisibility, setColumnVisibility] =
    useLocalState<VisibilityState>(
      `${name}TableColumnVisibility`,
      defaultColumnVisibility ?? {},
    );
  const processedColumns = useMemo(() => {
    const cols = columns.map((col) => {
      // If the column doesn't have a filterFn set, use arrayIncludes as default
      // This ensures NO_VALUE_MARKER works for all filterable columns
      if (!col.filterFn) {
        return {
          ...col,
          filterFn: "arrayIncludes",
        };
      }
      return col;
    });

    if (draggableRows) {
      cols.unshift({
        id: "drag-handle",
        header: "",
        cell: () => (
          <div className="drag-handle-cell">
            <Icon
              name="drag_indicator"
              style={{ cursor: "grab", verticalAlign: "text-top" }}
            />
          </div>
        ),
        meta: {
          style: { width: "40px", textAlign: "center" },
          excludeFromColumnsToggler: true,
        },
      } as any);
    }

    return cols;
  }, [columns, draggableRows]);

  const [columnOrder, setColumnOrder] = useLocalState<string[]>(
    `${name}TableColumnsOrder`,
    getDefaultColumnOrder(processedColumns),
  );
  const [columnFilters, setColumnFilters] = useSessionState<ColumnFiltersState>(
    `${name}TableColumnFilters`,
    defaultFiltering ?? [],
  );

  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const [tableKey, setTableKey] = useState(0);
  const pinnedRowIds = useMemo(
    () =>
      new Set([
        ...(data?.filter((r: any) => r.pinned).map((r: any) => r.id) ?? []),
      ]),
    [data],
  );
  const [rowPinning, setRowPinning] = useState<RowPinningState>({
    top: [...pinnedRowIds],
  });

  useEffect(() => {
    setTableKey((k) => k + 1);
  }, [pinnedRowIds]);

  const handleResetColumnSettings = () => {
    setColumnVisibility(defaultColumnVisibility ?? {});
    setColumnOrder(getDefaultColumnOrder(processedColumns));
  };

  const table = useReactTable<T>({
    data: data ?? [],
    columns: processedColumns,
    getRowId: (row: any, index: number) => row.id ?? index.toString(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: withPagination ? getPaginationRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedRowModel: getFacetedRowModel(),
    enableRowPinning: true,
    enableMultiRowSelection,
    enableRowSelection,
    state: {
      sorting,
      columnVisibility,
      columnOrder,
      rowSelection,
      rowPinning,
      columnFilters,
    },
    filterFns: {
      dateFilter: dateFilterFn,
      multiStringIncludes: multiStringIncludesFilterFn,
      arrayIncludes: arrayIncludesFilterFn,
      forcedBoolean: forcedBooleanFilterFn,
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onRowSelectionChange: setRowSelection,
    onRowPinningChange: setRowPinning,
    onColumnFiltersChange: setColumnFilters,
    initialState: {
      pagination: withPagination
        ? {
            pageIndex: 0,
            pageSize: Number(localStorage.getItem(`${name}PerPage`) || 15),
          }
        : undefined,
    },
  });
  const allColumns = useMemo(
    () =>
      table
        .getAllLeafColumns()
        .sort((a, b) => columnOrder.indexOf(a.id) - columnOrder.indexOf(b.id)),
    [columns, columnOrder],
  );

  // Only show pinned rows on the first page
  const pageIndex = table.getState().pagination?.pageIndex ?? 0;
  const pageSize = table.getState().pagination?.pageSize ?? 0;
  useEffect(() => {
    if (pageIndex === 0) {
      table.setRowPinning({
        top: [...pinnedRowIds],
      });
    } else {
      // Clear pinning on other pages
      table.setRowPinning({
        top: [],
      });
    }
  }, [pageIndex, table, pinnedRowIds]);

  const rows = useMemo(() => {
    if (isLoading || !data) return [];
    return [
      ...table.getTopRows(),
      ...table.getRowModel().rows.filter((row) => !pinnedRowIds.has(row.id)),
      ...table.getBottomRows(),
    ];
  }, [
    table.getRowModel,
    table.getTopRows,
    table.getBottomRows,
    isLoading,
    data,
    columns,
    sorting,
    pageIndex,
    pageSize,
    pinnedRowIds,
    rowPinning,
    columnFilters,
  ]);

  // PROBLEM: When we add new columns - they are not in the user's column order in localStorage
  // SOLUTION: Patch missing columns into the correct default positions and update localStorage
  useEffect(() => {
    const currentIds = table.getAllLeafColumns().map((col) => col.id);
    const missing = currentIds.filter((id) => !columnOrder.includes(id));

    if (missing.length > 0) {
      const defaultColumnOrder = getDefaultColumnOrder(processedColumns);
      const patchedOrder = [...columnOrder];

      for (const missingId of missing) {
        const defaultIndex = defaultColumnOrder.indexOf(missingId);

        if (defaultIndex === -1) {
          // unknown column, append to end
          patchedOrder.push(missingId);
        } else {
          // insert at the default index, or as close as possible
          let insertIndex = defaultIndex;
          while (
            insertIndex < patchedOrder.length &&
            defaultColumnOrder[insertIndex] &&
            patchedOrder.includes(defaultColumnOrder[insertIndex])
          ) {
            insertIndex++;
          }
          patchedOrder.splice(insertIndex, 0, missingId);
        }
      }

      setColumnOrder(patchedOrder);
    }
    // Columns can be added after mount, e.g. once the data they depend on loads
  }, [columnOrder, setColumnOrder, table, processedColumns]);

  // PROBLEM: When we add new hidden columns to the defaultColumnVisibility - they are not in the user's column visibility in localStorage and are therefore visible by default
  // SOLUTION: Patch missing hidden columns into the visibility and update localStorage
  useEffect(() => {
    if (!defaultColumnVisibility) return;

    const currentIds = columnVisibility ? Object.keys(columnVisibility) : [];
    const missingHiddenColumns = Object.keys(defaultColumnVisibility).filter(
      (id) => !currentIds.includes(id) && defaultColumnVisibility[id] === false, // not in currentIds and should be hidden by default
    );

    if (missingHiddenColumns.length > 0) {
      const patchedVisibility = { ...columnVisibility };
      for (const missingId of missingHiddenColumns) {
        patchedVisibility[missingId] = false;
      }
      setColumnVisibility(patchedVisibility);
    }
  }, [defaultColumnVisibility, columnVisibility, setColumnVisibility, table]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // PROBLEM: Selected rows are not being displayed in correct sorted order (for example for card printing).
  // SOLUTION: Expose the table instance and create a method that gets selected users in their current display order.
  useEffect(() => {
    if (!onTableReady) return;
    onTableReady(table);
  }, [table]);

  return (
    <>
      <div className={`${className} tableContainer ${dark ? "dark" : ""}`}>
        {(enableFiltering || enableColumnControls) && (
          <div className="tableControls">
            {enableFiltering && (
              <div
                style={{
                  display: "flex",
                  gap: "var(--wlcm-spacing-xs)",
                  flexWrap: "wrap",
                  alignItems: "center",
                  marginBottom: "var(--wlcm-spacing-xs)",
                }}
              >
                <AnimatePresence presenceAffectsLayout>
                  {columnFilters.map((filter) => {
                    const column = table.getColumn(filter.id);
                    const value = filter.value;

                    // Handle Date Filter Object
                    if (
                      typeof value === "object" &&
                      !Array.isArray(value) &&
                      value !== null
                    ) {
                      const dateFilter = value as any;

                      const parts = [];
                      if (dateFilter.start) {
                        parts.push(
                          new Date(dateFilter.start).toLocaleDateString(
                            i18n.language,
                          ),
                        );
                      }
                      if (dateFilter.end) {
                        parts.push(
                          new Date(dateFilter.end).toLocaleDateString(
                            i18n.language,
                          ),
                        );
                      }

                      const dateRange = parts.join(" - ");
                      const weekdayCount = dateFilter.weekdays?.length ?? 0;

                      let finalLabel = `${
                        (column?.columnDef.meta as { label?: string })?.label ||
                        column?.id
                      }: `;
                      if (dateRange) finalLabel += dateRange;

                      if (weekdayCount > 0) {
                        const weekdayText =
                          weekdayCount === 7
                            ? t("ALL_DAYS")
                            : `${dateFilter.weekdays?.map((day: number) => getWeekdayFromNumber(day, i18n.language, true)).join(", ")}`;
                        if (dateRange) finalLabel += ` (+ ${weekdayText})`;
                        else finalLabel += weekdayText;
                      }

                      return (
                        <motion.div key={filter.id} {...filterChipAnimation}>
                          <Chip
                            label={finalLabel}
                            active
                            onClick={() => {
                              column?.setFilterValue(undefined);
                            }}
                            style={{
                              cursor: "pointer",
                              background: "#7092eb80",
                            }}
                          />
                        </motion.div>
                      );
                    }

                    // Find getLabel() from column meta if available
                    const filterConfig = (column?.columnDef.meta as any)
                      ?.filterConfig;
                    const getLabel = filterConfig?.getLabel;

                    const values = (value as any[]) ?? [];
                    const valueLabels = values.map((val) => {
                      let label = String(val);
                      if (getLabel) {
                        label = String(getLabel(val, column));
                      } else if (val === NO_VALUE_MARKER) {
                        label = t("NO_VALUE");
                      } else if (val === HAS_VALUE_MARKER) {
                        label = t("HAS_VALUE");
                      } else if (typeof val === "boolean") {
                        label = val ? t("YES") : t("NO");
                      } else if (!val) {
                        label = t("NO_VALUE");
                      }
                      return label;
                    });

                    const columnMeta = column?.columnDef?.meta as
                      | { label?: string }
                      | undefined;
                    const headerDef = column?.columnDef?.header as unknown;
                    const headerString =
                      typeof headerDef === "string" ? headerDef : undefined;
                    const columnLabel =
                      columnMeta?.label ?? column?.id ?? headerString ?? "";

                    return (
                      <motion.div key={filter.id} {...filterChipAnimation}>
                        <Chip
                          label={
                            <>
                              <strong style={{ opacity: 0.5 }}>
                                {columnLabel ? `${columnLabel}:` : ""}
                              </strong>
                              {` ${valueLabels.join(", ")}`}
                            </>
                          }
                          active
                          onClick={() => {
                            column?.setFilterValue(undefined);
                          }}
                          style={{ cursor: "pointer", background: "#7092eb80" }}
                        />
                      </motion.div>
                    );
                  })}
                  <TableFilter
                    table={table}
                    rerenderDeps={[...(filterDeps ?? []), columnVisibility]}
                  />
                </AnimatePresence>
              </div>
            )}
            {enableColumnControls && (
              <div className="columnToggler">
                <Button
                  className={`table-column-toggler-${name}`}
                  circular
                  icon="columns"
                  onClick={() => setColumnPickerOpen(true)}
                >
                  <span className="sr-only">
                    {t("TABLE.COLUMN_PICKER.TITLE")}
                  </span>
                </Button>
                <Tooltip
                  anchorSelect={`.table-column-toggler-${name}`}
                  content={t("TABLE.COLUMN_PICKER.TITLE")}
                />
              </div>
            )}
          </div>
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event) => {
            const { active, over } = event;
            if (active.id !== over?.id && onRowOrderChange) {
              const oldIndex = rows.findIndex((row) => row.id === active.id);
              const newIndex = rows.findIndex((row) => row.id === over?.id);
              onRowOrderChange(oldIndex, newIndex);
            }
          }}
        >
          <SortableContext
            items={rows.map((row) => row.id)}
            strategy={verticalListSortingStrategy}
            disabled={!draggableRows}
          >
            <table key={tableKey} cellSpacing={0}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={
                          (
                            header.column.columnDef.meta as {
                              style?: CSSProperties;
                            }
                          )?.style
                        }
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {isLoading ? (
                  <tr className="noDataMessage">
                    <td colSpan={table.getAllColumns().length}>
                      <div className="center">
                        <LoadingSpinner />
                      </div>
                    </td>
                  </tr>
                ) : rows.length === 0 && noDataMessage ? (
                  <tr className="noDataMessage">
                    <td colSpan={table.getAllColumns().length}>
                      {noDataMessage}
                    </td>
                  </tr>
                ) : draggableRows ? (
                  rows.map((row) => (
                    <SortableRow
                      key={row.id}
                      row={row}
                      onRowClick={onRowClick}
                      onRowHover={onRowHover}
                      getRowClassName={getRowClassName}
                    />
                  ))
                ) : (
                  rows.map((row) => (
                    <tr
                      key={row.id}
                      style={!onRowClick ? { cursor: "unset" } : {}}
                      className={[
                        (row.original as any).pinned ? "pinnedRow" : "",
                        getRowClassName ? getRowClassName(row.original) : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          onMouseEnter={() => {
                            if (onRowHover) {
                              onRowHover(row.original);
                            }
                          }}
                          onClick={() => {
                            if (onRowClick) {
                              onRowClick(row.original);
                            }
                          }}
                          style={
                            (
                              cell.column.columnDef.meta as {
                                style?: CSSProperties;
                              }
                            )?.style
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </SortableContext>
        </DndContext>
        {withPagination && !!data?.length && (
          <TablePagination
            table={table}
            localStoragePageSizeKey={`${name}PerPage`}
            customPageSizeOptions={customPageSizeOptions}
          />
        )}
      </div>
      <PopupCard
        onClose={() => setColumnPickerOpen(false)}
        open={columnPickerOpen}
      >
        <h3>{t("TABLE.COLUMN_PICKER.TITLE")}</h3>
        <div
          style={{
            display: "flex",
            gap: "var(--wlcm-spacing-sm)",
            margin: "var(--wlcm-spacing-sm) 0",
          }}
        >
          <Button
            dark
            icon="visibility"
            onClick={() =>
              table.setColumnVisibility(
                Object.fromEntries(allColumns.map((col) => [col.id, true])),
              )
            }
          >
            {t("TABLE.COLUMN_PICKER.SHOW_ALL")}
          </Button>
          <Button
            dark
            icon="visibility_off"
            onClick={() =>
              table.setColumnVisibility(
                Object.fromEntries(allColumns.map((col) => [col.id, false])),
              )
            }
          >
            {t("TABLE.COLUMN_PICKER.HIDE_ALL")}
          </Button>
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event) => {
            const { active, over } = event;
            if (active.id !== over?.id) {
              const oldIndex = columnOrder.indexOf(active.id as string);
              const newIndex = columnOrder.indexOf(over?.id as string);
              setColumnOrder((items) => arrayMove(items, oldIndex, newIndex));
            }
          }}
        >
          <SortableContext
            items={columnOrder}
            strategy={verticalListSortingStrategy}
          >
            <ol style={{ listStyle: "none", padding: 0 }}>
              {allColumns.map((column) =>
                (
                  column.columnDef.meta as {
                    excludeFromColumnsToggler?: boolean;
                  }
                )?.excludeFromColumnsToggler || column.id === "actions" ? (
                  <></>
                ) : (
                  <ColumnToggler key={column.id} column={column} />
                ),
              )}
            </ol>
          </SortableContext>
        </DndContext>
        <Button
          dark
          icon="undo"
          onClick={handleResetColumnSettings}
          style={{
            width: "max-content",
            marginTop: "var(--wlcm-spacing-sm)",
          }}
        >
          {t("RESET")}
        </Button>
      </PopupCard>
    </>
  );
}

function SortableRow<T>({
  row,
  onRowClick,
  onRowHover,
  getRowClassName,
}: {
  row: any;
  onRowClick?: (rowData: T) => void;
  onRowHover?: (rowData: T) => void;
  getRowClassName?: (rowData: T) => string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.id });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: onRowClick ? "pointer" : "unset",
    position: isDragging ? "relative" : (undefined as any),
    zIndex: isDragging ? 2 : (undefined as any),
    background: isDragging ? "#B7C9F5" : (undefined as any),
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`${(row.original as any).pinned ? "pinnedRow" : ""} ${isDragging ? "dragging" : ""} ${getRowClassName?.(row.original) || ""}`}
    >
      {row.getVisibleCells().map((cell: any) => {
        const isDragHandle = cell.column.id === "drag-handle";
        return (
          <td
            key={cell.id}
            onMouseEnter={() => {
              if (onRowHover) {
                onRowHover(row.original);
              }
            }}
            onClick={() => {
              if (onRowClick && !isDragHandle) {
                onRowClick(row.original);
              }
            }}
            style={
              (
                cell.column.columnDef.meta as {
                  style?: CSSProperties;
                }
              )?.style
            }
            {...(isDragHandle ? { ...attributes, ...listeners } : {})}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        );
      })}
    </tr>
  );
}

function ColumnToggler<T>({ column }: { column: Column<T, unknown> }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    padding: "var(--wlcm-spacing-xs)",
    borderRadius: "var(--wlcm-border-radius-xs)",
    background: isDragging ? "var(--wlcm-color-light-black)" : "none",
    touchAction: "none",
    display: "flex",
    alignItems: "center",
  };

  return (
    <li ref={setNodeRef} style={style} {...attributes}>
      <span
        {...listeners}
        style={{ cursor: "grab", paddingRight: "var(--wlcm-spacing-sm)" }}
      >
        <Icon name="drag_indicator" />
      </span>
      <Toggle
        dark
        label={
          (column.columnDef.meta as { label?: string })?.label ?? column.id
        }
        checked={column.getIsVisible()}
        onChange={column.toggleVisibility}
        stopPropagation
      />
    </li>
  );
}

export default Table;
